<template>
  <!--
    Répartition garçons / filles de la classe.

    Comptée depuis la liste d'élèves déjà chargée par l'écran : aucun appel
    réseau de plus, et le décompte suit automatiquement un ajout, une
    suppression ou un changement de classe.
  -->
  <div class="card gender-card">
    <div class="card-header pb-0 p-3">
      <h6 class="mb-0">{{ $t("classdetails.gender_title") }}</h6>
    </div>

    <div class="card-body p-3 pt-2">
      <p class="gender-empty mb-0" v-if="!total">
        {{ $t("classdetails.gender_empty") }}
      </p>

      <template v-else>
        <div class="row g-2">
          <div class="col-6">
            <div class="tile tile--boys">
              <span class="tile-icon"><i class="bi bi-gender-male"></i></span>
              <span class="tile-count">{{ boys }}</span>
              <span class="tile-label">{{ $t("classdetails.boys") }}</span>
            </div>
          </div>
          <div class="col-6">
            <div class="tile tile--girls">
              <span class="tile-icon"><i class="bi bi-gender-female"></i></span>
              <span class="tile-count">{{ girls }}</span>
              <span class="tile-label">{{ $t("classdetails.girls") }}</span>
            </div>
          </div>
        </div>

        <!--
          La barre dit d'un coup d'œil ce que deux nombres obligent à
          comparer. Elle ne s'affiche qu'avec au moins un élève de sexe
          connu : sinon elle serait vide et trompeuse.
        -->
        <div class="split" v-if="boys || girls">
          <div class="split-bar">
            <span class="split-boys" :style="{ width: boysPercent + '%' }"></span>
            <span class="split-girls" :style="{ width: 100 - boysPercent + '%' }"></span>
          </div>
          <div class="split-legend">
            <span>{{ boysPercent }}&nbsp;%</span>
            <span>{{ 100 - boysPercent }}&nbsp;%</span>
          </div>
        </div>

        <!--
          Les élèves sans sexe renseigné ne sont pas passés sous silence :
          sans cette ligne, la somme des deux tuiles ne tomberait pas sur
          l'effectif de la classe et on croirait à une erreur de comptage.
        -->
        <p class="gender-note mb-0" v-if="unknown">
          <i class="bi bi-info-circle me-1"></i>
          {{ $t("classdetails.gender_unknown", { count: unknown }) }}
        </p>

        <p class="gender-total mb-0">
          {{ $t("classdetails.gender_total", { count: total }) }}
        </p>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "class-gender-card",
  props: {
    /** Liste d'élèves de la classe, telle que l'écran l'a chargée. */
    students: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    /**
     * Sexe normalisé d'un élève : "M", "F", ou "" si l'information manque.
     *
     * La valeur vient soit du formulaire, qui n'écrit que "M" ou "F", soit
     * d'un import de tableur, où la colonne est saisie à la main — on y
     * trouve aussi bien « Masculin » que « f ». On ne retient donc que
     * l'initiale, en majuscule.
     */
    counts() {
      const tally = { M: 0, F: 0, other: 0 };
      (this.students || []).forEach((student) => {
        const initial = String((student || {}).gender || "")
          .trim()
          .charAt(0)
          .toUpperCase();
        if (initial === "M") tally.M += 1;
        else if (initial === "F") tally.F += 1;
        else tally.other += 1;
      });
      return tally;
    },
    boys() {
      return this.counts.M;
    },
    girls() {
      return this.counts.F;
    },
    unknown() {
      return this.counts.other;
    },
    total() {
      return (this.students || []).length;
    },
    /** Part des garçons, sur les seuls élèves dont le sexe est connu. */
    boysPercent() {
      const known = this.boys + this.girls;
      if (!known) return 0;
      return Math.round((this.boys / known) * 100);
    },
  },
};
</script>

<style scoped>
.gender-card {
  min-height: 240px;
}

.tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 14px 8px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
}

.tile-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  margin-bottom: 4px;
}

.tile--boys .tile-icon {
  background: rgba(94, 152, 228, 0.16);
  color: #5e98e4;
}

.tile--girls .tile-icon {
  background: rgba(228, 94, 160, 0.16);
  color: #e45ea0;
}

.tile-count {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
}

.tile-label {
  font-size: 12px;
  opacity: 0.65;
}

.split {
  margin-top: 14px;
}

.split-bar {
  display: flex;
  height: 7px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
}

.split-boys {
  background: #5e98e4;
}

.split-girls {
  background: #e45ea0;
}

.split-legend {
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  font-size: 11px;
  opacity: 0.6;
}

.gender-note,
.gender-total {
  margin-top: 10px;
  font-size: 11.5px;
  opacity: 0.6;
}

.gender-empty {
  font-size: 13px;
  opacity: 0.6;
  text-align: center;
  padding: 28px 12px;
}
</style>
